import { postTypeTitle } from '@lib/utils'
import { useRouter } from 'next/router'
import Head from 'next/head'
import { Trans, t } from '@lingui/macro'
import { AdjustmentsIcon } from '@heroicons/react/solid'
import Index from 'components/Index'
import H1 from 'components/ui/H1'
import Header from 'components/ui/Header'
import LeadingParagraph from 'components/ui/LeadingParagraph'
import IndexPagination from 'components/IndexPagination'
import PublicationsTabs from 'components/PublicationsTabs'
import EventsTabs from 'components/EventsTabs'
import { useSearch } from 'providers/SearchProvider'
import { useTranslations } from 'providers/TranslationsProvider'
import { useEffect, useState } from 'react'
import PostTypeTitle from 'components/PostTypeTitle'
import FormSelect from 'components/ui/FormSelect'
import { formatDate } from '@lib/i18n'

const PostsIndex = ({
  posts,
  type,
  variant = '',
  page,
  totalPages,
  totalPosts,
  start,
  end,
  showType = false,
  year = null,
  month = null,
}) => {
  const router = useRouter()
  const [archiveYear, setArchiveYear] = useState(year ? parseInt(year) : null)
  const [archiveMonth, setArchiveMonth] = useState(
    month ? parseInt(month) : null
  )
  const { init, headLinks } = useTranslations()
  const { setIsDialogOpen, setPostIndexType } = useSearch(type)

  useEffect(() => {
    if (type && type !== 'search') {
      setPostIndexType(type)
    }
  })

  useEffect(() => {
    init()
  }, [type])

  const getArchiveYears = () => {
    const opts = [
      {
        value: '',
        text: t`Year`,
      },
    ]
    for (let i = new Date().getFullYear(); i >= 2008; i--) {
      opts.push({
        value: i,
        text: i,
      })
    }
    return opts
  }

  const getArchiveMonths = () => {
    const opts = [
      {
        value: '',
        text: t`Month`,
      },
    ]
    for (let i = 1; i <= 12; i++) {
      opts.push({
        value: i,
        text: formatDate(new Date(2020, i - 1, 1), 'MMM', router.locale),
      })
    }
    return opts
  }

  const gotoArchive = () => {
    let year = new Date().getFullYear()
    if (archiveYear) {
      year = archiveYear
    }
    if (archiveMonth) {
      router.push(
        `/${type}${
          type === 'events' ? `/past` : variant !== '' ? `/${variant}` : ``
        }/${String(year)}/${String(archiveMonth).padStart(2, `0`)}`
      )
    } else {
      router.push(
        `/${type}${
          type === 'events' ? `/past` : variant !== '' ? `/${variant}` : ``
        }/${String(year)}`
      )
    }
  }

  return (
    <div className="container pb-10">
      <Head>
        <title key="title">
          {`${postTypeTitle(`${type}_${variant}`)} - ${
            process.env.NEXT_PUBLIC_SITE_TITLE
          }`}
        </title>
        <meta
          property="og:title"
          content={postTypeTitle(`${type}_${variant}`)}
          key="og:title"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}${
            router.locale == 'en' ? `` : `/${router.locale}`
          }/${type}/${variant ? variant : ``}`}
          key="canonical"
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_DOMAIN}${
            router.locale == 'en' ? `` : `/${router.locale}`
          }/${type}/${variant ? variant : ``}`}
          key="og:url"
        />
        {headLinks()}
      </Head>
      <Header>
        {type === 'publications' && <PublicationsTabs variant={variant} />}
        {type === 'events' && <EventsTabs variant={variant} />}
        <div className="relative">
          <H1>
            <PostTypeTitle type={type} variant={variant} />
          </H1>
          {router.query.q && (
            <div className="mt-4 text-center text-primary-300 md:mt-6 lg:mt-10">
              &ldquo;{router.query.q}&rdquo;
            </div>
          )}
          <div className="absolute top-0 right-0 -mr-4 pb-8 md:-mr-0 lg:pb-16 2xl:pb-24">
            <button
              type="button"
              onClick={() => setIsDialogOpen(true)}
              className="flex flex-row rounded-full bg-primary-100 p-2 text-primary-500 hover:bg-opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 lg:rounded-lg lg:px-4"
            >
              <AdjustmentsIcon className="h-5 w-5" />
              <span className="ml-2 hidden lg:block">
                <Trans>Advanced Search</Trans>
              </span>
            </button>
          </div>
        </div>
      </Header>
      {type === 'publications' && variant === 'idpc' && (
        <LeadingParagraph>
          <Trans>
            A key role and added value of IDPC is the production of policy
            analysis for a broad range of audiences including academia, civil
            society and policy makers. The majority of these publications are
            collaborations with members and partners, and are translated into
            multiple languages to support and facilitate advocacy.
          </Trans>
        </LeadingParagraph>
      )}
      <div className="mb-4 text-center md:text-left">
        <Trans>
          Results {start < 1 ? 1 : start + 1} to{' '}
          {totalPosts < end ? totalPosts : end} of {totalPosts}
        </Trans>
        {(type !== 'events' || (type === 'events' && variant === 'past')) && (
          <form onSubmit={gotoArchive}>
            <div className="flex flex-row items-center justify-start">
              <div className="mr-1">
                <FormSelect
                  options={getArchiveYears()}
                  value={archiveYear}
                  onChange={(event) => setArchiveYear(event.target.value)}
                />
              </div>
              <div className="mr-1">
                <FormSelect
                  options={getArchiveMonths()}
                  value={archiveMonth}
                  onChange={(event) => setArchiveMonth(event.target.value)}
                />
              </div>
              <div className="mt-1">
                <button className="flex items-center justify-center rounded border border-transparent bg-accent-500 px-2 py-0.5 text-center text-sm font-medium text-white">
                  Go
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
      {posts && posts.length ? (
        <>
          <Index
            posts={posts}
            showType={showType}
            firstLarge={type !== 'events' && parseInt(page) === 1}
          />
          <IndexPagination
            totalPages={totalPages}
            page={page}
            type={type}
            year={year}
            month={month}
            variant={variant}
            start={start}
            end={end}
            totalPosts={totalPosts}
          />
        </>
      ) : (
        <div className="container max-w-3xl pb-10">
          <div className="mb-4 rounded-lg border border-primary-100 bg-white px-6 py-6 shadow-sm">
            <p className="text-xl font-medium">
              <Trans>No results found.</Trans>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default PostsIndex
